import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Input from '../../components/common/Input';
import { validateRequired, getPasswordValidation } from '../../utils/validators';
import api from '../../services/api';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const invalidLink = !uid || !token;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
    if (apiError) setApiError('');
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.password)) {
      newErrors.password = 'Password is required';
    } else {
      const pwValidation = getPasswordValidation(formData.password);
      if (!pwValidation.isValid) {
        newErrors.password = pwValidation.errors[0];
      }
    }

    if (!validateRequired(formData.confirmPassword)) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      await api.post('/auth/password-reset/confirm/', {
        uid,
        token,
        new_password: formData.password,
      });
      setSubmitted(true);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 429) {
        setApiError('Too many attempts. Please wait a minute and try again.');
      } else if (Array.isArray(data?.error)) {
        setApiError(data.error.join(' '));
      } else if (data?.error) {
        setApiError(data.error);
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-pw">
      <div className="reset-pw__panel">
        <div className="reset-pw__container">

          <div className="reset-pw__header">
            <h2 className="reset-pw__title">Set New Password</h2>
            <p className="reset-pw__subtitle">Deep Roots Milling &mdash; Wholesale Portal</p>
          </div>

          {invalidLink ? (
            <div className="reset-pw__invalid">
              <div className="reset-pw__invalid-icon">!</div>
              <h3 className="reset-pw__invalid-heading">Invalid Reset Link</h3>
              <p className="reset-pw__invalid-text">
                This password reset link is invalid or incomplete. Please request
                a new one.
              </p>
              <Link to="/forgot-password" className="reset-pw__action-link">
                Request New Reset Link
              </Link>
            </div>
          ) : submitted ? (
            <div className="reset-pw__success">
              <div className="reset-pw__success-icon">&#10003;</div>
              <h3 className="reset-pw__success-heading">Password Updated</h3>
              <p className="reset-pw__success-text">
                Your password has been reset successfully. You can now sign in
                with your new password.
              </p>
              <Link to="/login" className="reset-pw__action-link">
                Sign In &rarr;
              </Link>
            </div>
          ) : (
            <>
              {apiError && (
                <div className="reset-pw__error" role="alert">{apiError}</div>
              )}

              <form className="reset-pw__form auth-form" onSubmit={handleSubmit} noValidate>
                <Input
                  type="password"
                  name="password"
                  label="New Password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  error={errors.password}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  required
                  autoFocus
                />

                <Input
                  type="password"
                  name="confirmPassword"
                  label="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  error={errors.confirmPassword}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                />

                <button
                  type="submit"
                  className="reset-pw__submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>

              <div className="reset-pw__footer">
                <p>
                  <Link to="/login" className="reset-pw__link">
                    &larr; Back to Sign In
                  </Link>
                </p>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
